import React, { useState } from 'react';
import { FaQuestionCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO.jsx';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What is Bicom Bioresonance?",
      answer: "Bicom bioresonance is described as a gentle, non-invasive complementary wellbeing approach that works with bioinformational or energetic signals. Sessions are designed to support awareness, balance and overall wellbeing in a personalised and respectful way."
    },
    {
      question: "How does a Bicom Bioresonance session work?",
      answer: "Every visit begins with a conversation about you – your experiences, lifestyle and what you would like to explore. During the session, you may hold electrodes or they may be comfortably placed on the body while the Bicom system works with bioinformational signals. Many visitors describe the experience as calm, relaxing and easy to follow."
    },
    {
      question: "What should I expect from my first appointment?",
      answer: "If it is your first time, you can expect a calm, respectful and unhurried environment. Everything is explained clearly, you can ask questions at any time, and nothing happens without your understanding. The aim is to create clarity and comfort, especially for those who are new to this approach."
    },
    {
      question: "Is Bicom Bioresonance safe?",
      answer: "Bicom bioresonance is generally considered a gentle, non-invasive complementary approach that does not involve needles or pharmaceuticals. At our centre we use EU-certified equipment that is regularly maintained and updated, and we work within professional and ethical boundaries at all times. Bioresonance is not a medical treatment and should not replace professional healthcare."
    },
    {
      question: "Is bioresonance scientifically proven?",
      answer: "Bioresonance is used by many people as a complementary wellbeing approach. At the same time, it remains a topic of discussion within parts of the scientific and medical communities. We believe everyone should have transparent information and the freedom to decide whether they wish to explore it."
    },
    {
      question: "Will you diagnose or give medical advice?",
      answer: "No. Bioresonance is a complementary wellbeing approach and is not equivalent to medical diagnostics or laboratory testing. Observations made during a session do not confirm the presence of disease, pathogens or medical conditions. Individual interpretations and experiences may vary. Our work is not intended to diagnose, prescribe or replace conventional healthcare. If you have symptoms or health concerns, we always recommend consulting a qualified medical professional."
    },
    {
      question: "Why do people choose your centre instead of others?",
      answer: "Since 2017, our work has been built around three core values: knowledge, personal attention and respect for individual boundaries. Visitors often say the difference lies not only in the technology but in how they are treated — with time, clear communication and a calm professional atmosphere. Continuous education and ethical practice help people feel confident they are in responsible and experienced hands."
    },
    {
      question: "Do you use the same programme for everyone?",
      answer: "No. We recognise that every person is different. Sessions are adjusted individually and in real time according to the person in front of us."
    },
    {
      question: "I am sceptical – should I still book?",
      answer: "Many visitors felt the same before their first appointment. Curiosity does not require belief — only a willingness to explore and make an informed decision based on your own experience."
    },
    {
      question: "How will I feel after the session?",
      answer: "Experiences vary from person to person. Some people report feeling relaxed or more aware of their body, while others simply appreciate taking time for themselves. There is no single expected outcome."
    },
    {
      question: "How many sessions do people usually have?",
      answer: "Some people attend once, while others include sessions as part of their ongoing wellbeing routine. There is never any obligation or pressure to continue."
    },
    {
      question: "I am worried someone will try to sell me additional products.",
      answer: "We understand this concern. Our focus is on the session itself, clear information and your comfort. Any further steps are always your personal choice."
    },
    {
      question: "Can children attend?",
      answer: "Yes, families are welcome. The way we work is always adapted carefully to age, sensitivity and individual comfort."
    },
    {
      question: "Do I need to prepare for my visit?",
      answer: "Being well hydrated and arriving with an open mind is usually the best preparation. If you wish, you may also bring notes about your lifestyle or previous experiences."
    },
    {
      question: "Why is the session fee what it is?",
      answer: "Your session includes dedicated time, trained practitioners, certified equipment, ongoing professional education and an individual approach. Many people value the experience of being listened to carefully and treated with respect."
    },
    {
      question: "How can I book?",
      answer: "You can contact us by phone or through the website. If you prefer, you are welcome to ask questions first and decide afterwards."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/10 to-gold/5">
      <SEO
        title="FAQ"
        description="Frequently asked questions about BICOM bioresonance, sessions, safety and what to expect. Bioresonance Leicester – 108 New Walk, Leicester."
        path="/faq"
      />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <FaQuestionCircle className="mr-2" />
              Frequently Asked Questions
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-6xl font-bold text-dark-grey mb-6"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Frequently Asked Questions – Bicom Bioresonance
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-xl text-dark-grey leading-relaxed"
            >
              Find answers to common questions about our bioresonance therapy services
            </motion.p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white rounded-2xl shadow-lg border border-gold/20 overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gold/5 transition-colors duration-200"
                  aria-expanded={openIndex === index}
                >
                  <h3 className="text-lg md:text-xl font-bold text-dark-grey pr-4" style={{ fontFamily: "Playfair Display, serif" }}>
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0">
                    {openIndex === index ? (
                      <FaChevronUp className="text-gold text-xl" />
                    ) : (
                      <FaChevronDown className="text-gold text-xl" />
                    )}
                  </div>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-5 pt-2 border-t border-gold/10">
                        <p className="text-gray-600 leading-relaxed text-base md:text-lg">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
              Still Have Questions?
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              We're here to help. Feel free to contact us with any additional questions or concerns you may have.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:07761374278"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Call Tomasz: 0776 137 4278
              </a>
              <a
                href="tel:07923825242"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Call Pawel: 0792 382 5242
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default FAQ;
